import { mapGetters, mapMutations, mapState, mapActions } from 'vuex';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
    debounceTime,
    map,
    distinctUntilChanged,
    tap,
    filter,
    delayWhen,
    pairwise,
    scan,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
    PANEL_OPTION_KEYS,
    WIDGET_TYPES,
    CHART_TYPES,
    GRAPH_FILTER_TYPES,
    GRAPH_OPTION_DEFAULT_VALUE,
    IFRAME_EVENT_MESSAGE,
    FILTER_WIDGET_OPTIONS_DEFAULT_VALUE,
} from '@consts';

import { isValueExist, listIncludes } from '@util';
import {
    BASIC_ROW_LIST,
    refineSourceName,
    isCustomMetric,
    DATE_GROUP_ROWS,
    checkSourceTableMode,
    checkIsAnalyticSource,
    GoalWidgetMode,
    sortLongString,
    refineChannel,
    refineSourceNameWithDeprecated,
    blendingActivated,
    mappingActivated,
} from '@/utils/dashboard';
import {
    getUpdateViewSyncedFromDashboardToReportMode,
    getUpdateViewSyncedFromReportToDashboard,
} from '@/utils/reportMode';
import { VIEW_PATH_MAP } from '@/utils/dashboard';

import DateFilterSetting from '@components/Dashboard/common/WidgetSettingPanel/options/dateFilter';
import { NewDashboardFilterSetting } from '@components/Dashboard/common/WidgetSettingPanel/options';
import CreativeBreakdownSetting from '@/components/Dashboard/common/WidgetSettingPanel/options/creativeBreakdown';
import DataSourceSetting, {
    UNSELECT_ALREADY_SELECTED,
} from '@components/Dashboard/common/WidgetSettingPanel/options/dataSource';
import GraphOptionsSetting from '@components/Dashboard/common/WidgetSettingPanel/options/graphOptions';
import GraphDisplayOptionsSetting from '@components/Dashboard/common/WidgetSettingPanel/options/graphOptions/displayMenu';
import TableOptionsSetting from '@components/Dashboard/common/WidgetSettingPanel/options/tableOptions';
import ForecastSetting from '@components/Dashboard/common/WidgetSettingPanel/options/forecast';
import MetricFilterSetting from '@/components/Dashboard/common/WidgetSettingPanel/options/metricFilter';
import ComparisonByDateSetting from '@/components/Dashboard/common/WidgetSettingPanel/options/comparisonByDate';
import CreativeCntOption from '@/components/Dashboard/common/WidgetSettingPanel/options/creativeCnt';
import GraphGridToggleOption from '@/components/Dashboard/common/WidgetSettingPanel/options/graphGridToggleOption';
import GraphLabelToggleOption from '@/components/Dashboard/common/WidgetSettingPanel/options/graphLabelToggleOption';
import TrendlineToggleOption from '@/components/Dashboard/common/WidgetSettingPanel/options/trendlineToggleOption';
import FilterWidgetOptions from '@/components/Dashboard/common/WidgetSettingPanel/options/filterWidgetOptions';
import {
    FILTER_WIDGET_FIELD_VALUE_SUBTYPE,
    FILTER_WIDGET_LABEL_SUBTYPE,
    normalizeWidgetDropdownConfig,
    serializeWidgetDropdownConfig,
    stripFieldValueModeFields,
    stripLabelModeFields,
} from '@/components/Dashboard/common/WidgetSettingPanel/options/filterWidgetOptions/dropdownOptions/dropdownConfig';
import BlendSettingToggleOption from '@/components/Dashboard/common/WidgetSettingPanel/options/blendSettingToggleOption';
import TableTransposeToggleOption from '@/components/Dashboard/common/WidgetSettingPanel/options/tableTransposeToggleOption';
import AxesConfigOption from '@/components/Dashboard/common/WidgetSettingPanel/options/axesConfigOption';
import ComboAxesConfigOption from '@/components/Dashboard/common/WidgetSettingPanel/options/comboAxesConfigOption';
import QuickMenuVisibleToggleOption from '@/components/Dashboard/common/WidgetSettingPanel/options/quickMenuVisibleToggle';
import Header from './components/Header';

/**
 * migrated options
 */
import {
    MetricQuickControl,
    MultiBreakdownSetting,
    MultiMetricSetting,
    SingleBreakdownSetting,
    SingleMetricSetting,
    GoalMetricsSetting,
} from './reactOptions';

import {
    FIELD_KEY,
    makeBreakdownFormat,
    makeMetricFormat,
    pluckFieldKey,
} from '@components/Dashboard/common/WidgetSettingPanel/reactOptions/utils';
import { FIELD_PANEL_KEY } from '@components/Dashboard/common/WidgetSettingPanel/reactOptions/consts';

import Drawer from '@/components/Dashboard/common/Drawer';
import RingLoader from '@/components/Common/RingLoader';

import currentViewMixin from '@/mixins/currentView';
import iframeMixin from '@/mixins/iframe';
import metricTranslatorMixin from '@/mixins/translator/metric';
import breakdownTranslatorMixin from '@/mixins/translator/breakdown';

import moment from 'moment';

import * as S from './styles';
import { numeral } from '@util';
import {
    resolveTeamspaceAllowedChannelIds,
    resolveTeamspaceAllowedAssetIds,
} from '@/utils/filter';
import { getDefaultShowGraphLabel, isExcludedForLinkedWidget } from './utils';
import { DashboardService } from '@/services';
import { OnOffSetting } from './options/OnOff';
import withHMR from '@/utils/withHMR';
import {
    CREATIVE_EXPOSURE_KEY_CONFIG,
    GRAPH_EXPOSURE_KEY_CONFIG,
    EXPOSURE_KEY_CONFIG,
} from './reactOptions/quickControl/constants';
import {
    makeExposureData,
    makeComboExposureData,
} from './reactOptions/quickControl/utils';

const {
    WORD_CLOUD,
    COLOR_CLUSTERING,
    EMOJI_ANALYSIS,
    FUNNEL,
    HORIZONTAL_BAR,
    BAR,
    LINE,
    PIE,
    DONUT,
    CHOROPLETH_MAP,
} = CHART_TYPES;

export const {
    BREAKDOWN,
    CREATIVE_BREAKDOWN,
    WIDGET_FILTER,
    DATA_SOURCE,
    SINGULAR_METRIC,
    TABLE_METRIC,
    GRAPH_OPTIONS,
    GRAPH_DISPLAY_OPTIONS,
    TABLE_OPTIONS,
    DATE_FILTER,
    GOAL_METRICS,
    FORECAST,
    METRIC_FILTER,
    COMPARISON_BY_DATE,
    ADVANCED_METRIC_FILTER,
    ONOFF,
    CREATIVE_CNT,
    GRAPH_GRID,
    GRAPH_LABEL,
    TRENDLINE,
    FILTER_WIDGET_OPTIONS,
    BLEND_SETTING_TOGGLE,
    TABLE_TRANSPOSE_TOGGLE,
    AXES_CONFIG,
    COMBO_AXES_CONFIG,
    SORTING_METRIC_EXPOSURE,
    QUICK_MENU_VISIBLE,
} = PANEL_OPTION_KEYS;

const {
    ALARMS,
    PIVOT_TABLE,
    PIVOT_TABLE_ANALYTIC,
    GRAPH,
    OVERVIEW,
    OVERVIEW_CHILD,
    CREATIVE,
    NOTEPAD,
    SINGLE_METRIC,
    SMALL_TABLE,
    GOAL,
    GOAL_CHILD,
    AB_TESTING,
    FILTER,
} = WIDGET_TYPES;

const SINGLE_BREAKDOWN_WIDGET_TYPES = new Set([
    GRAPH,
    SMALL_TABLE,
    SINGLE_METRIC,
    OVERVIEW,
]);

const BLEND_ENABLE_WIDGET_TYPES = [
    GRAPH,
    OVERVIEW,
    SINGLE_METRIC,
    SMALL_TABLE,
    GOAL,
    CREATIVE,
];

const OPTION_MAP = {
    [BREAKDOWN]: MultiBreakdownSetting,
    [CREATIVE_BREAKDOWN]: CreativeBreakdownSetting,

    [SINGULAR_METRIC]: SingleMetricSetting,
    [TABLE_METRIC]: MultiMetricSetting,
    [GOAL_METRICS]: GoalMetricsSetting,
    [GRAPH_OPTIONS]: GraphOptionsSetting,
    [SORTING_METRIC_EXPOSURE]: MetricQuickControl,

    [WIDGET_FILTER]: NewDashboardFilterSetting,
    [DATA_SOURCE]: DataSourceSetting,
    [ONOFF]: OnOffSetting,
    [GRAPH_DISPLAY_OPTIONS]: GraphDisplayOptionsSetting,
    [TABLE_OPTIONS]: TableOptionsSetting,
    [DATE_FILTER]: DateFilterSetting,
    [FORECAST]: ForecastSetting,
    [METRIC_FILTER]: MetricFilterSetting,
    [COMPARISON_BY_DATE]: ComparisonByDateSetting,
    [CREATIVE_CNT]: CreativeCntOption,
    [GRAPH_GRID]: GraphGridToggleOption,
    [GRAPH_LABEL]: GraphLabelToggleOption,
    [TRENDLINE]: TrendlineToggleOption,
    [FILTER_WIDGET_OPTIONS]: FilterWidgetOptions,
    [BLEND_SETTING_TOGGLE]: BlendSettingToggleOption,
    [TABLE_TRANSPOSE_TOGGLE]: TableTransposeToggleOption,
    [AXES_CONFIG]: AxesConfigOption,
    [COMBO_AXES_CONFIG]: ComboAxesConfigOption,
    [QUICK_MENU_VISIBLE]: QuickMenuVisibleToggleOption,
};

const SETTING_OPTIONS_MAP = {
    [PIVOT_TABLE]: [
        DATA_SOURCE,
        BREAKDOWN,
        TABLE_METRIC,
        WIDGET_FILTER,
        METRIC_FILTER,
        DATE_FILTER,
        COMPARISON_BY_DATE,
        TABLE_OPTIONS,
        TABLE_TRANSPOSE_TOGGLE,
    ],
    [PIVOT_TABLE_ANALYTIC]: [
        DATA_SOURCE,
        BREAKDOWN,
        TABLE_METRIC,
        DATE_FILTER,
        COMPARISON_BY_DATE,
        TABLE_OPTIONS,
        TABLE_TRANSPOSE_TOGGLE,
    ],
    [SINGLE_METRIC]: [
        DATA_SOURCE,
        SINGULAR_METRIC,
        WIDGET_FILTER,
        DATE_FILTER,
        BLEND_SETTING_TOGGLE,
    ],
    [OVERVIEW]: [
        DATA_SOURCE,
        TABLE_METRIC,
        WIDGET_FILTER,
        ONOFF,
        DATE_FILTER,
        BLEND_SETTING_TOGGLE,
    ],
    [ALARMS]: [],
    [NOTEPAD]: [],
    [GOAL]: [
        DATA_SOURCE,
        GOAL_METRICS,
        WIDGET_FILTER,
        DATE_FILTER,
        FORECAST,
        BLEND_SETTING_TOGGLE,
    ],
    [GRAPH]: [
        DATA_SOURCE,
        GRAPH_OPTIONS,
        TRENDLINE,
        GRAPH_DISPLAY_OPTIONS,
        AXES_CONFIG,
        COMBO_AXES_CONFIG,
        QUICK_MENU_VISIBLE,
        GRAPH_GRID,
        GRAPH_LABEL,
        WIDGET_FILTER,
        METRIC_FILTER,
        DATE_FILTER,
        BLEND_SETTING_TOGGLE,
    ],
    [CREATIVE]: [
        DATA_SOURCE,
        SORTING_METRIC_EXPOSURE,
        CREATIVE_BREAKDOWN,
        TABLE_METRIC,
        WIDGET_FILTER,
        CREATIVE_CNT,
        METRIC_FILTER,
        DATE_FILTER,
        BLEND_SETTING_TOGGLE,
    ],
    [SMALL_TABLE]: [
        DATA_SOURCE,
        BREAKDOWN,
        SINGULAR_METRIC,
        WIDGET_FILTER,
        METRIC_FILTER,
        DATE_FILTER,
        BLEND_SETTING_TOGGLE,
    ],
    [AB_TESTING]: [TABLE_METRIC, WIDGET_FILTER],
    [FILTER]: [FILTER_WIDGET_OPTIONS],
};

export const LOCALE_MAP = {
    [DATA_SOURCE]: 'source',
    [FORECAST]: 'column',
    [SINGULAR_METRIC]: 'column',
    [TABLE_METRIC]: 'column',
    [GOAL_METRICS]: 'column',
    [FORECAST]: 'column',
    [SORTING_METRIC_EXPOSURE]: 'sorting_column',
    [ONOFF]: 'column',
};

export const cleanParam = R.map(R.omit(['refinedKey', 'selected']));

const assocSelected = R.assoc('selected', true);

export const makeAvailableBreakdownFormat = (v) => ({
    ...v,
    id: v.name,
    uniqueKey: v.id || v.name,
});

export const setMetricUniqueKey = (v) => {
    const uniqueKey = isCustomMetric(v) ? v.name : v.category + v.name;

    return {
        ...v,
        uniqueKey,
    };
};
export const makeIndexKey = (key) => {
    switch (key) {
        case DATA_SOURCE:
            return 'assetId';
        default:
            return 'name';
    }
};

const makeGoalMetricFormat = (childWidget) => {
    const { metric, ...rest } = R.pickAll([
        'metric',
        'goal',
        'order',
        'id',
        'goalInfo',
        'goalRange',
    ])(childWidget);

    return {
        ...makeMetricFormat(metric),
        ...rest,
    };
};

/**
 * Generates an index key for adAccounts based on channel and channelId.
 *
 * When an account is reconnected, the channelId remains the same while the assetId changes.
 * Using this function to create an index key allows us to maintain existing account settings
 * after reconnection by relying on the stable channelId rather than the volatile assetId.
 *
 * @param {Object} account - The account object containing channel and channelId
 * @returns {string} A composite key in the format "channel-channelId"
 */
export const getAccountKey = ({ channel, channelId }) =>
    `${channel}-${channelId}`;
export const getAccountKeyWithDisplayChannelName = ({
    displayChannelName,
    channelId,
}) => `${displayChannelName}-${channelId}`;

export const filterSelected = R.filter(R.prop('selected'));
export const getUniqueSelectedIds = R.compose(
    R.uniq,
    R.pluck('id'),
    filterSelected
);
const getSelectedAdAccounts = R.compose(filterSelected);
export const getSelectedChannels = (adAccounts) => {
    const selectedChannels = R.map(refineChannel, adAccounts);
    const set = new Set(selectedChannels);
    return [...set];
};

const SOURCE_CONNECTED_WIDGET_TYPES = R.compose(
    (set) => set.add(OVERVIEW_CHILD),
    (v) => new Set(Object.keys(v)),
    R.filter((v) => v.includes(DATA_SOURCE))
)(SETTING_OPTIONS_MAP);

export const AUTO_TIME_WIDGET_TYPES = new Set([OVERVIEW_CHILD, GRAPH]);

// const SMALL_TABLE_BREAKDOWNS = R.keys(SMALL_TABLE_MAP);

const DEFAULT_DATE_FILTER = {
    disabled: true,
    type: 'range',
    values: {
        range: {
            from: moment().subtract(7, 'd').format('YYYY-MM-DD'),
            to: moment().format('YYYY-MM-DD'),
        },
    },
};

const sortWidgetForCompare = (widget) => {
    const isFunnel =
        widget.type === WIDGET_TYPES.GRAPH &&
        widget.subType === CHART_TYPES.FUNNEL;

    return R.evolve({
        adAccounts: R.compose(sortLongString, R.pluck('channelId')),
        columns: isFunnel ? R.identity : sortLongString,
        metricWhereConditions: R.sortBy(R.prop('field')),
    })(widget);
};

const checkIsWidgetEqual = (a, b) =>
    R.equals(sortWidgetForCompare(a), sortWidgetForCompare(b));

const BASIC_ROW_WITHOUT_DATE = R.reject(
    (row) => DATE_GROUP_ROWS.includes(row),
    BASIC_ROW_LIST
);

const makeNumberFromComma = (v) => numeral(v).value();

const makeNumberFromFilter = R.map(R.evolve({ value: makeNumberFromComma }));

const WidgetSettingPanel = {
    name: 'WidgetSettingPanel',
    mixins: [
        currentViewMixin,
        iframeMixin,
        metricTranslatorMixin,
        breakdownTranslatorMixin,
    ],
    props: {
        isTemplateView: Boolean,
        pendingExposureListKey: {
            type: String,
            default: null,
        },
    },
    watch: {
        sourceList: {
            handler() {
                const { widgetType, watchDataSource } = this;

                const isSourceConnceted =
                    SOURCE_CONNECTED_WIDGET_TYPES.has(widgetType);

                if (!isSourceConnceted) return;

                watchDataSource();
            },
            deep: true,
        },
        isCustomWidget: {
            handler(newValue, oldValue) {
                if (oldValue === undefined) return;

                this.resetDataSource();
                this.resetDataOnsource(true);
            },
        },
    },
    data() {
        return {
            widgetDateFilter: {},
            widgetComparisonDateFilter: {},
            dashboardFilters: [],
            widgetFilters: [],
            metricFilters: [],
            advancedMetricFilters: [],
            sourceList: [],
            breakdownList: [],
            creativeDimensionIds: [],
            creativeAvailableDimensionList: [],
            metricList: [],
            exposureListMap: {},
            exposureSelectedFieldMap: {},
            breakdownUpdated: false,
            inPreparation: false,
            graphOptions: GRAPH_OPTION_DEFAULT_VALUE,
            secondPanelVisibility$: new BehaviorSubject(),
            forecastEnabled: false,
            hideGraph: false,
            showGraphGrid: true,
            showGraphLabel: undefined,
            showTrendline: false,
            quickMenuVisible: true,
            isCustomWidget: undefined,
            activeOutsideClick: true,
            comparisonByDate: false,
            columnDimensionIds: [], // 새로 추가된 필드
            isTransposed: false,
            [CREATIVE_CNT]: 10,
            iframeListeners_: [
                {
                    eventType: IFRAME_EVENT_MESSAGE.SAVE_WIDGET_DATE_FILTER,
                    listener: (e) => {
                        const { data } = e;
                        const { dateFilter = {}, comparisonDateFilter = {} } =
                            data;

                        this.widgetDateFilter = dateFilter;
                        this.widgetComparisonDateFilter = comparisonDateFilter;
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.UPDATE_WIDGET_FILTER,
                    listener: (e) => {
                        const { data } = e;
                        const { groupFilters = [] } = data;

                        this.updateState({
                            key: WIDGET_FILTER,
                            value: groupFilters,
                        });

                        this.setIsWidgetFilterOpen(false);
                    },
                },
            ],
            secondPanelLoading: false,
            filterWidgetOptions: FILTER_WIDGET_OPTIONS_DEFAULT_VALUE,
        };
    },
    provide() {
        return {
            secondPanelVisibility$: this.secondPanelVisibility$,
        };
    },
    computed: {
        ...mapState('newDashboard', [
            'tableAdAccounts',
            'teamspaceAllowedChannelIds',
            'teamspaceAllowedAssetIds',
            'dimensions',
            'headerPanelLoading',
            'selectedDataSources$',
            'dataSources',
            'views',
        ]),
        ...mapState('dashboardHeader', ['widgetRefresh$', 'gridShowTotalRow']),
        ...mapState('printPDF', ['isReportMode']),
        ...mapGetters('newDashboard', [
            'editingWidgetId',
            'editingChildWidgetId',
            'currentWidgetsById',
            'adAccounts',
            'analyticAccounts',
            'currentViewConfig',
            'currentView',
            'currentWidgets',
            'adsTableSources',
            'dateFilterProps',
            'tableAdAccountsByChannelId',
            'tableAdAccountsByAccountKey',
            'tableAdAccountsByDisplayChannelNameChannelId',
            'datasourceMapByAssetId',
        ]),
        ...mapGetters('dashboardHeader', [
            'editableHeaderList',
            'updateEditableHeaderList',
        ]),
        ...mapGetters('user', ['pricePlanSettings', 'account']),
        ...mapState('user', ['userAccountId']),
        isFieldValueFilterWidgetEnabled() {
            return this.account?.meta?.allowFieldValueFilterWidget === true;
        },
        teamspaceName() {
            const title =
                R.path(['teamspace', 'viewTitle'], this.currentView) ||
                this.currentView.teamspaceName;
            const name =
                R.path(['teamspace', 'name'], this.currentView) ||
                this.currentView.teamspaceName;

            if (title || name) return title || name;

            // Fallback: traverse view tree to find nearest teamspace ancestor
            const views = this.views || [];
            const pathMap = VIEW_PATH_MAP({ data: views });
            const path = pathMap[this.currentView.id] || [];

            let teamspaceName = '';
            let node = views;

            path.forEach((idx) => {
                node = Array.isArray(node) ? node[idx] : undefined;
                if (!node) return;

                if (node.type === 'teamspace') {
                    teamspaceName =
                        node.viewTitle ||
                        node.teamspaceName ||
                        R.path(['teamspace', 'name'], node) ||
                        R.path(['teamspace', 'viewTitle'], node) ||
                        '';
                }

                node = node.children;
            });

            return teamspaceName || '';
        },
        targetWidget() {
            const { currentWidgetsById, editingWidgetId } = this;
            return currentWidgetsById[editingWidgetId];
        },
        editingWidget() {
            const {
                currentWidgetsById,
                editingWidgetId: id,
                editingChildWidgetId: childId,
            } = this;

            const widget = currentWidgetsById[id] || {};

            return childId
                ? R.compose(
                      R.find(R.propEq('id', childId)),
                      R.propOr([], 'childWidgets')
                  )(widget)
                : widget;
        },
        widgetId: R.pathOr('', ['editingWidget', 'id']),
        widgetType: R.cond([
            [
                R.pathEq(['editingWidget', 'subType'], 'analytic'),
                R.always(PIVOT_TABLE_ANALYTIC),
            ],
            [
                R.pathEq(['editingWidget', 'subType'], 'abTesting'),
                R.always(AB_TESTING),
            ],
            [R.T, R.pathOr('', ['editingWidget', 'type'])],
        ]),
        selectedGraphType: R.ifElse(
            R.propSatisfies(R.equals(GRAPH), 'widgetType'),
            R.pathOr('', ['graphOptions', 'selectedGraphType']),
            R.always('')
        ),
        isColumnLikeGraph: R.propSatisfies(
            listIncludes([BAR, LINE, HORIZONTAL_BAR]),
            'selectedGraphType'
        ),
        isGraphDisplayOptionsAvailable: R.propSatisfies(
            listIncludes([PIE, DONUT, BAR, HORIZONTAL_BAR, LINE]),
            'selectedGraphType'
        ),
        isFunnel: R.propEq('selectedGraphType', FUNNEL),
        isNoBreakdownGraphType: R.propSatisfies(
            listIncludes([
                WORD_CLOUD,
                COLOR_CLUSTERING,
                EMOJI_ANALYSIS,
                FUNNEL,
                CHOROPLETH_MAP,
            ]),
            'selectedGraphType'
        ),
        isGraphWidget: R.pathEq(['editingWidget', 'type'], GRAPH),
        isTable() {
            return (
                this.widgetType === PIVOT_TABLE ||
                this.widgetType === PIVOT_TABLE_ANALYTIC
            );
        },
        widgetMode() {
            if (this.widgetType === PIVOT_TABLE) return 'ad';
            if (this.widgetType === PIVOT_TABLE_ANALYTIC) return 'analytic';

            /**
             * `isAnalytic` is persisted as `isCustomWidget` in the very same
             * payload that stores `adAccounts`, and `adAccounts` comes from the
             * unscoped `sourceList` (`getSelectedAdAccounts`). Deriving the mode
             * from the scoped selection would store a mode contradicting the
             * sources saved next to it: an empty scoped selection falls to
             * 'toggleable', which resolves `isAnalytic` to the widget's own
             * `isCustomWidget` flag and flips analytic widgets that never
             * carried one back to marketing.
             */
            return checkSourceTableMode(this.selectedSource);
        },
        isAnalytic() {
            if (this.widgetMode === 'toggleable') {
                return this.isCustomWidget;
            }

            return this.widgetMode === 'analytic';
        },
        exposureKeyConfig() {
            return this.widgetType === CREATIVE
                ? CREATIVE_EXPOSURE_KEY_CONFIG
                : GRAPH_EXPOSURE_KEY_CONFIG;
        },
        panelLoading() {
            return this.headerPanelLoading[this.widgetId];
        },
        /**
         * `sourceOptions` before the teamspace datasource scope is applied.
         *
         * The widget-type narrowing and the sorting are domain rules unrelated
         * to the scope, so both variants share them; only the
         * `folderFilteredAssetIds` filter belongs to `sourceOptions` alone.
         * Readers that feed the stored widget must use this one — see
         * `selectedSourceIgnoringScope`.
         */
        sourceOptionsIgnoringScope() {
            let list = this.tableAdAccounts;

            /**
             *  Blend Toggle Option 없는 테이블 위젯은 사용 가능한 데이터 소스만 표시
             * 1. Generic 테이블: 제네릭 데이터 소스만 표시
             *    - 단, mapping이 활성화된 제네릭 소스는 마케팅 소스처럼 동작하므로 제외
             * 2. Marketing 테이블: 모든 데이터 소스 표시
             *    - 마케팅 데이터 소스
             *    - Blendable 데이터 소스 (모든 제네릭 소스)
             *    - Mappable 데이터 소스
             */
            if (this.isTable) {
                if (this.isAnalytic) {
                    list = R.filter(
                        R.allPass([
                            checkIsAnalyticSource,
                            R.complement(mappingActivated),
                        ])
                    )(list);
                }
            }

            // datasource option sorting
            return R.sortBy(
                (v) =>
                    R.compose(R.toLower, refineChannel)(v) +
                    R.compose(R.toLower, refineSourceName)(v),
                list
            );
        },
        sourceOptions() {
            const { sourceOptionsIgnoringScope, folderFilteredAssetIds } = this;

            if (folderFilteredAssetIds === null) {
                return sourceOptionsIgnoringScope;
            }

            return R.filter(
                (v) => folderFilteredAssetIds.includes(Number(v.assetId)),
                sourceOptionsIgnoringScope
            );
        },
        folderFilteredAdAccountIds() {
            return resolveTeamspaceAllowedChannelIds(
                this.teamspaceAllowedChannelIds
            );
        },
        folderFilteredAssetIds() {
            const resolved = resolveTeamspaceAllowedAssetIds(
                this.teamspaceAllowedAssetIds
            );

            return resolved === null ? null : resolved.map(Number);
        },
        folderFilteredSources() {
            const { sourceList, folderFilteredAssetIds } = this;

            if (folderFilteredAssetIds !== null) {
                return R.filter(
                    (v) => folderFilteredAssetIds.includes(Number(v.assetId)),
                    sourceList
                );
            }

            return sourceList;
        },
        selectedSource() {
            const {
                widgetType,
                targetWidget,
                sourceOptions,
                folderFilteredSources,
            } = this;

            return widgetType === OVERVIEW_CHILD
                ? targetWidget.adAccounts || sourceOptions
                : filterSelected(folderFilteredSources);
        },
        /**
         * Selection as the user left it, ignoring the teamspace datasource scope.
         *
         * A RESTRICTED teamspace that allows zero channels makes `selectedSource`
         * empty even though the user deselected nothing, so scope-driven emptiness
         * must never be read as "no source selected". This mirrors the preservation
         * policy of `options/dataSource` (`visuallyHiddenList`): the scope hides
         * sources, it does not delete the selection.
         *
         * The OVERVIEW_CHILD branch mirrors `selectedSource` but falls back to
         * `sourceOptionsIgnoringScope`: falling back to the scoped `sourceOptions`
         * would make this computed scoped too whenever `targetWidget.adAccounts`
         * is nullish, silently breaking the contract for every reader below.
         */
        selectedSourceIgnoringScope() {
            const {
                widgetType,
                targetWidget,
                sourceOptionsIgnoringScope,
                sourceList,
            } = this;

            return widgetType === OVERVIEW_CHILD
                ? targetWidget.adAccounts || sourceOptionsIgnoringScope
                : filterSelected(sourceList);
        },
        /**
         * Goes out on the wire (`fetchCreativeAvailableDimensionList` POSTs it
         * to /dataSource/creativeWidgetAvailableDimensions), so it stays
         * scoped: the teamspace scope must never be widened by a request this
         * panel makes. The stored creative breakdown is protected on the write
         * side instead — see `isSelectionNarrowedByScope`.
         */
        selectedAssetIds: R.compose(
            R.pluck('assetId'),
            R.propOr([], 'selectedSource')
        ),
        /**
         * True while the teamspace scope hides part of the stored selection —
         * including the case where it hides all of it.
         *
         * Anything derived from the scoped selection is then a verdict about a
         * strict subset of what the widget owns: narrower input can only yield
         * narrower results, so writing those results back deletes settings the
         * user never touched and cannot restore. Display and requests stay
         * scoped; writes are skipped while this holds.
         */
        isSelectionNarrowedByScope() {
            const { selectedSource, selectedSourceIgnoringScope } = this;

            return selectedSource.length !== selectedSourceIgnoringScope.length;
        },
        hasMultiBreakdownSetting() {
            return (
                !isValueExist(this.columnDimensionIds) &&
                !this.comparisonByDate &&
                !SINGLE_BREAKDOWN_WIDGET_TYPES.has(this.widgetType)
            );
        },
        refinedOptions() {
            const {
                widgetType,
                isNoBreakdownGraphType,
                isFunnel,
                isGraphDisplayOptionsAvailable,
                hasAnyIncludeTotalSource,
                selectedGraphType,
                isAnalytic,
            } = this;

            let optionList = SETTING_OPTIONS_MAP[widgetType] || [];

            if (widgetType === GRAPH) {
                if (isNoBreakdownGraphType) {
                    optionList = R.without([BREAKDOWN], optionList);
                }

                if (isFunnel) {
                    optionList = [...optionList, TABLE_METRIC];
                }

                if (selectedGraphType === DONUT || selectedGraphType === PIE) {
                    optionList = R.insert(4, METRIC_FILTER, optionList);
                }

                if (!isGraphDisplayOptionsAvailable) {
                    optionList = R.without([GRAPH_DISPLAY_OPTIONS], optionList);
                }
            }

            if (widgetType === PIVOT_TABLE_ANALYTIC) {
                optionList = R.insert(3, WIDGET_FILTER, optionList);
            }

            if (widgetType == SINGLE_METRIC || widgetType === OVERVIEW) {
                if (hasAnyIncludeTotalSource) {
                    optionList = R.insert(1, BREAKDOWN, optionList);
                }
            }

            if (isAnalytic) {
                optionList = R.without([METRIC_FILTER], optionList);
            }

            const optionMap = {
                ...OPTION_MAP,
                [BREAKDOWN]: this.hasMultiBreakdownSetting
                    ? MultiBreakdownSetting
                    : SingleBreakdownSetting,
            };

            return R.compose(Object.entries, R.pick(optionList))(optionMap);
        },
        currentBreakdownIds() {
            const { widgetType, breakdownList, creativeDimensionIds } = this;

            const dimensionIds = pluckFieldKey(breakdownList);

            if (widgetType !== CREATIVE) {
                return dimensionIds;
            }

            return creativeDimensionIds;
        },
        /**
         * Gates both the BREAKDOWN option (`refinedOptions`) and, far more
         * importantly, `R.dissoc('dimensionIds')` in `updateSingleMetricWidget`
         * / `updateOverviewGraphWidget`. `requestClose()` calls `updateWidget()`
         * unconditionally, so a false here erases the stored breakdown on a mere
         * open-and-close. Judge on the unscoped selection: a teamspace scope
         * that hides every selected source (0 allowed channels, or an allow list
         * that no longer intersects the widget's sources) must not be read as
         * "this widget has no total-capable source".
         */
        hasAnyIncludeTotalSource: R.allPass([
            R.compose(
                R.any(R.prop('includesBreakdownForTotal')),
                R.propOr([], 'selectedSource')
            ),
            R.prop('isAnalytic'),
        ]),

        isAllValidBlended() {
            const { selectedSource } = this;

            return R.all(blendingActivated)(selectedSource);
        },
    },
    methods: {
        ...mapActions('newDashboard', [
            'saveWidget',
            'saveView',
            'saveCurrentViewToServer',
            'getTableAdAccounts',
        ]),
        ...mapMutations('dashboardHeader', ['setVisible', 'resetWidgetData']),
        ...mapMutations('newDashboard', [
            'setHeaderPanelLoading',
            'setIsWidgetFilterOpen',
        ]),
        ...mapActions('iframe', ['postIframeMessage']),
        _saveWidget({ widget }) {
            if (this.isReactDashboard) {
                this.postIframeMessage({
                    origin: 'VUE',
                    type: IFRAME_EVENT_MESSAGE.SAVE_WIDGET,
                    data: { widget },
                });
                return Promise.resolve();
            }

            return this.saveWidget({ widget });
        },
        _saveCurrentViewToServer() {
            if (this.isReactDashboard) {
                this.postIframeMessage({
                    origin: 'VUE',
                    type: IFRAME_EVENT_MESSAGE.SAVE_CURRENT_VIEW_TO_SERVER,
                });
                return;
            }

            this.saveCurrentViewToServer();
        },
        _saveView({ view }) {
            if (this.isReactDashboard) {
                this.postIframeMessage({
                    origin: 'VUE',
                    type: IFRAME_EVENT_MESSAGE.SAVE_VIEW,
                    data: {
                        param: { view },
                    },
                });
                return;
            }

            this.saveView({ view });
        },
        closePanel() {
            if (this.isReactDashboard) {
                this.$emit('close');
                return;
            }

            this.setVisible(false);
        },
        requestClose() {
            const { requestClose, updateWidget } = this;
            const filterOptions = this.$refs[FILTER_WIDGET_OPTIONS];

            if (filterOptions?.hasUnsavedOpenPanel?.()) {
                filterOptions.requestCloseWithConfirm(requestClose);
                return;
            }

            updateWidget();
        },
        updateWidget() {
            const {
                updateTableWidget,
                updateOverviewChildWidget,
                updateOverviewGraphWidget,
                updateGraphWidget,
                updateCreativeWidget,
                updateSingleMetricWidget,
                updateSmallTableWidget,
                updateFilterWidget,
                editingWidget: { type },
            } = this;

            switch (type) {
                case OVERVIEW:
                    return updateOverviewGraphWidget();
                case OVERVIEW_CHILD:
                    return updateOverviewChildWidget();
                case PIVOT_TABLE:
                    return updateTableWidget();
                case CREATIVE:
                    return updateCreativeWidget();
                case SINGLE_METRIC:
                    return updateSingleMetricWidget();
                case GRAPH:
                    return updateGraphWidget();
                case SMALL_TABLE:
                    return updateSmallTableWidget();
                case GOAL:
                    this.updateGoalWidget();
                    return;
                case FILTER:
                    return updateFilterWidget();
                default:
                    return;
            }
        },
        removeOverviewChildWidget({ name }) {
            const {
                _saveWidget,
                targetWidget,
                editingChildWidgetId: childId,
            } = this;

            const childWidgets = targetWidget.childWidgets || [];

            const targetIndex = R.findIndex(
                R.propEq('metric', name),
                childWidgets
            );

            if (targetIndex < 0) return;

            const updatedChildWidgets = R.remove(targetIndex, 1, childWidgets);

            const updatedWidget = R.assoc(
                'childWidgets',
                updatedChildWidgets,
                targetWidget
            );

            _saveWidget({ widget: updatedWidget }).then(() => {
                this._saveCurrentViewToServer();

                /**
                 * close panel if deleted custom metric
                 * is eqaul to editing child widget
                 */
                const editingWidgetIndex = R.findIndex(
                    R.propEq('id', childId),
                    updatedChildWidgets
                );

                if (editingWidgetIndex < 0) {
                    this.closePanel();
                }
            });
        },
        updateSingleMetricWidget() {
            const {
                targetWidget: widget,
                dashboardFilters,
                widgetFilters,
                _saveWidget,
                sourceList,
                widgetDateFilter,
                widgetComparisonDateFilter,
                metricList,
                breakdownList,
                hasAnyIncludeTotalSource,
                isAnalytic,
            } = this;

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            const updatedMetric = R.compose(R.head, pluckFieldKey)(metricList);
            const dimensionIds = pluckFieldKey(breakdownList);

            const updatedWidget = R.compose(
                R.ifElse(
                    () => hasAnyIncludeTotalSource,
                    R.assoc('dimensionIds', dimensionIds),
                    R.dissoc('dimensionIds')
                ),
                R.assoc('adAccounts', adAccounts),
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.assoc('filterTagList', dashboardFilters),
                R.assoc('groupFilters', widgetFilters),
                R.assoc('metric', updatedMetric),
                R.assoc('dateFilter', widgetDateFilter),
                R.assoc('comparisonDateFilter', widgetComparisonDateFilter),
                R.assoc('isCustomWidget', isAnalytic)
            )(widget);

            const isEqual = checkIsWidgetEqual(widget, updatedWidget);

            if (!isEqual) {
                // note: when a metric has alias, initialize to empty, whenever metric is updated
                const widgetTobeSaved = R.assoc('alias', '', updatedWidget);
                _saveWidget({ widget: widgetTobeSaved });
            }

            this.closePanel();
        },
        updateOverviewGraphWidget() {
            const {
                editingWidget: widget,
                metricList,
                updateEditableHeaderList,
                sourceList,
                dashboardFilters,
                widgetFilters,
                widgetDateFilter: dateFilter,
                widgetComparisonDateFilter: comparisonDateFilter,
                _saveWidget,
                breakdownList,
                hasAnyIncludeTotalSource,
                hideGraph,
                isAnalytic,
            } = this;

            updateEditableHeaderList({ value: metricList });

            const columns = pluckFieldKey(metricList);
            const dimensionIds = pluckFieldKey(breakdownList);

            const updatedChildWidgets = columns.map((column, index) => {
                const existingWidget = (widget.childWidgets || [])[index];

                if (existingWidget) {
                    if (existingWidget.metric === column) {
                        return existingWidget;
                    } else {
                        return R.mergeLeft(
                            {
                                metric: column,
                                alias: '',
                            },
                            existingWidget
                        );
                    }
                } else {
                    return {
                        alias: '',
                        id: uuidv4(),
                        metric: column,
                        type: OVERVIEW_CHILD,
                    };
                }
            });

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            const updatedWidget = R.compose(
                R.ifElse(
                    () => hasAnyIncludeTotalSource,
                    R.assoc('dimensionIds', dimensionIds),
                    R.dissoc('dimensionIds')
                ),
                R.assoc('childWidgets', updatedChildWidgets),
                R.assoc('filterTagList', dashboardFilters),
                R.assoc('groupFilters', widgetFilters),
                R.assoc('adAccounts', adAccounts),
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.assoc('dateFilter', dateFilter),
                R.assoc('comparisonDateFilter', comparisonDateFilter),
                R.assoc('hideGraph', hideGraph),
                R.assoc('isCustomWidget', isAnalytic)
            )(widget);

            const isEqual = checkIsWidgetEqual(widget, updatedWidget);

            if (!isEqual) {
                _saveWidget({ widget: updatedWidget });
            }

            this.closePanel();
        },
        updateOverviewChildWidget() {
            const {
                editingChildWidgetId: childId,
                _saveWidget,
                targetWidget: widget,
                dashboardFilters,
                widgetFilters,
                sourceList,
                metricList,
            } = this;

            const targetIndex = R.findIndex(
                R.propEq('id', childId),
                widget.childWidgets
            );

            const setChild = (key, value) =>
                R.set(R.lensPath(['childWidgets', targetIndex, key]), value);

            const adAccounts = getSelectedAdAccounts(sourceList);
            const updatedMetric = R.compose(R.head, pluckFieldKey)(metricList);

            const updatedWidget = R.compose(
                setChild('filterTagList', dashboardFilters),
                setChild('groupFilters', widgetFilters),
                setChild('metric', updatedMetric),
                setChild('adAccounts', adAccounts)
            )(widget);

            const pathToChild = R.pathOr({}, ['childWidgets', targetIndex]);

            const isEqual = checkIsWidgetEqual(
                pathToChild(widget),
                pathToChild(updatedWidget)
            );

            if (!isEqual) {
                // note: when a metric has alias, initialize to empty, whenever metric is updated
                const widgetTobeSaved = setChild('alias', '')(updatedWidget);
                _saveWidget({ widget: widgetTobeSaved });
            }

            this.closePanel();
        },
        updateTableWidget() {
            const {
                editingWidget: origin,
                _saveWidget,
                metricList,
                breakdownList,
                metricFilters,
                dashboardFilters,
                widgetFilters,
                sourceList,
                postUpdateTableWidget,
                updateEditableHeaderList,
                widgetDateFilter: dateFilter,
                widgetComparisonDateFilter: comparisonDateFilter,
                gridShowTotalRow,
                widgetType,
                comparisonByDate,
                advancedMetricFilters,
                isTransposed,
                columnDimensionIds,
            } = this;

            updateEditableHeaderList({ value: metricList });

            const columns = pluckFieldKey(metricList);
            const dimensionIds = pluckFieldKey(breakdownList);

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            const widget = R.compose(
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.when(
                    () => widgetType !== AB_TESTING,
                    R.compose(
                        R.assoc('filterTagList', dashboardFilters),
                        R.assoc('groupFilters', widgetFilters)
                    )
                ),
                R.mergeLeft({
                    columns,
                    dimensionIds,
                    adAccounts,
                    dateFilter,
                    comparisonDateFilter,
                    gridShowTotalRow,
                    metricWhereConditions: metricFilters,
                    comparisonByDate,
                    columnDimensionIds,
                    advancedMetricWhereConditions: makeNumberFromFilter(
                        advancedMetricFilters
                    ),
                    isTransposed,
                })
            )(origin);

            _saveWidget({ widget });

            postUpdateTableWidget({
                origin,
                dimensionIds,
                updatedWidget: widget,
            });
        },
        updateSmallTableWidget() {
            const {
                editingWidget: origin,
                metricList,
                metricFilters,
                dashboardFilters,
                widgetFilters,
                breakdownList,
                sourceList,
                _saveWidget,
                postUpdateTableWidget,
                widgetDateFilter: dateFilter,
                widgetComparisonDateFilter: comparisonDateFilter,
                advancedMetricFilters,
                isAnalytic,
            } = this;

            const columns = pluckFieldKey(metricList);
            const dimensionIds = pluckFieldKey(breakdownList);

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            const widget = R.compose(
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.mergeLeft({
                    columns,
                    dimensionIds,
                    adAccounts,
                    dateFilter,
                    comparisonDateFilter,
                    filterTagList: dashboardFilters,
                    groupFilters: widgetFilters,
                    metricWhereConditions: metricFilters,
                    advancedMetricWhereConditions: makeNumberFromFilter(
                        advancedMetricFilters
                    ),
                    isCustomWidget: isAnalytic,
                })
            )(origin);

            postUpdateTableWidget({
                origin,
                dimensionIds,
                updatedWidget: widget,
            });
            _saveWidget({ widget });
        },
        updateCreativeWidget() {
            const {
                targetWidget: widget,
                metricList,
                sourceList,
                metricFilters,
                dashboardFilters,
                widgetFilters,
                currentViewConfig,
                currentWidgets,
                widgetDateFilter: dateFilter,
                widgetComparisonDateFilter: comparisonDateFilter,
                currentBreakdownIds,
                advancedMetricFilters,
                creativeCnt,
                isAnalytic,
                exposureSelectedFieldMap,
                exposureListMap,
            } = this;

            const { id } = widget;

            const columns = pluckFieldKey(metricList);

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            const { fieldListKey: sortingFieldListKey } =
                EXPOSURE_KEY_CONFIG[SORTING_METRIC_EXPOSURE];

            // exposureSelectedFieldMap[SORTING_METRIC_EXPOSURE]은 string 타입
            const sortByName =
                exposureSelectedFieldMap[SORTING_METRIC_EXPOSURE] ?? '';
            const sort = R.assoc(
                'name',
                sortByName,
                widget.sort || { isDesc: true }
            );

            // exposureListMap[sortingFieldListKey]: string[] | null | undefined
            // null/undefined → 미설정(widget.exposureList 유지), string[] → 저장
            const sortingList = exposureListMap[sortingFieldListKey];
            const exposureList = Array.isArray(sortingList)
                ? {
                      [sortingFieldListKey]: sortingList,
                  }
                : undefined;

            const updatedWidget = R.compose(
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.mergeLeft({
                    adAccounts,
                    columns,
                    dateFilter,
                    comparisonDateFilter,
                    dimensionIds: currentBreakdownIds,
                    filterTagList: dashboardFilters,
                    groupFilters: widgetFilters,
                    metricWhereConditions: metricFilters,
                    advancedMetricWhereConditions: makeNumberFromFilter(
                        advancedMetricFilters
                    ),
                    creativeCnt,
                    isCustomWidget: isAnalytic,
                    sort,
                    exposureList,
                })
            )(widget);

            let currentLayout = R.pathOr(
                [],
                ['viewLayout', 'layout'],
                currentViewConfig
            );

            let pageGridId = -1;
            if (this.isReportMode) {
                const reportModeLayout = R.path(['reportModeLayout'])(
                    this.currentViewConfig
                );
                for (const [pageKey, reportMode] of Object.entries(
                    reportModeLayout
                )) {
                    const hasWidget = R.compose(
                        R.find(R.propEq('i', id)),
                        R.prop('layout')
                    )(reportMode);

                    if (hasWidget) {
                        pageGridId = pageKey;
                        currentLayout = R.prop('layout')(reportMode);
                        break;
                    }
                }
            }

            const targetLayoutIndex = R.findIndex(R.propEq('i', id))(
                currentLayout
            );

            const targetWidgetIndex = R.findIndex(R.propEq('id', id))(
                currentWidgets
            );

            const updatedLayout = R.assocPath(
                [targetLayoutIndex, 'h'],
                11 + columns.length,
                currentLayout
            );

            const updatedWidgets = R.assocPath(
                [targetWidgetIndex],
                updatedWidget,
                currentWidgets
            );

            // view layout 에 문제 있음
            let updatedView = this.currentView;

            if (this.isReportMode) {
                updatedView = getUpdateViewSyncedFromReportToDashboard(
                    updatedView,
                    updatedLayout,
                    updatedWidgets,
                    pageGridId
                );
            } else {
                updatedView = getUpdateViewSyncedFromDashboardToReportMode(
                    updatedView,
                    updatedLayout,
                    updatedWidgets
                );
            }

            this._saveView({ view: updatedView });

            this.closePanel();
        },
        updateFilterWidget() {
            const { filterWidgetOptions, targetWidget: widget } = this;
            const normalizedFilterWidgetOptions =
                !this.isFieldValueFilterWidgetEnabled &&
                filterWidgetOptions?.subType ===
                    FILTER_WIDGET_FIELD_VALUE_SUBTYPE
                    ? {
                          ...filterWidgetOptions,
                          subType: FILTER_WIDGET_LABEL_SUBTYPE,
                          groupId: undefined,
                          by: undefined,
                          condition: undefined,
                          values: [],
                      }
                    : filterWidgetOptions;
            const serializedFilterWidgetOptions = serializeWidgetDropdownConfig(
                normalizedFilterWidgetOptions
            );
            const baseWidget =
                serializedFilterWidgetOptions.subType ===
                FILTER_WIDGET_FIELD_VALUE_SUBTYPE
                    ? stripFieldValueModeFields(widget)
                    : stripLabelModeFields(widget);
            let updatedWidget = R.mergeLeft(
                serializedFilterWidgetOptions,
                baseWidget
            );
            const isFieldValueWidget =
                serializedFilterWidgetOptions.subType ===
                FILTER_WIDGET_FIELD_VALUE_SUBTYPE;
            const hasByChanged = !R.equals(
                serializedFilterWidgetOptions.by,
                widget.by
            );
            const hasGroupChanged =
                serializedFilterWidgetOptions.groupId !== widget.groupId;
            const hasConditionChanged = !R.equals(
                serializedFilterWidgetOptions.condition,
                widget.condition
            );
            const shouldResetValuesForFieldValue =
                isFieldValueWidget &&
                (hasByChanged || hasGroupChanged || hasConditionChanged);

            const getValues = R.propOr([], 'values');
            const getSlicedValues = R.compose(R.slice(0, 1), getValues);

            if (shouldResetValuesForFieldValue) {
                updatedWidget = R.mergeLeft(
                    { values: [], isAllSelected: false },
                    updatedWidget
                );
            }

            if (!normalizedFilterWidgetOptions.allowMultiSelect) {
                const values = getSlicedValues(updatedWidget);
                updatedWidget = R.mergeLeft(
                    { values, isAllSelected: false },
                    updatedWidget
                );
            }

            if (
                serializedFilterWidgetOptions.subType !==
                FILTER_WIDGET_FIELD_VALUE_SUBTYPE
            ) {
                const normalizedAvailableLabels =
                    normalizedFilterWidgetOptions?.availableLabels || [];
                const availableLabelIdsSet = new Set(
                    normalizedAvailableLabels
                        .filter((label) => label?.type !== 'breakdown')
                        .map((label) => label.id)
                );
                const availableBreakdownIdsSet = new Set(
                    normalizedAvailableLabels
                        .filter((label) => label?.type === 'breakdown')
                        .map((label) => label.id)
                );

                const isAvailableLabel = (value) =>
                    availableLabelIdsSet.has(value.labelId) ||
                    availableBreakdownIdsSet.has(value.customBreakdownId);
                const filterValuesByAvailableLabels =
                    R.filter(isAvailableLabel);
                updatedWidget = R.assoc(
                    'values',
                    R.compose(
                        filterValuesByAvailableLabels,
                        getValues
                    )(updatedWidget),
                    updatedWidget
                );
            }
            this._saveWidget({ widget: updatedWidget });
            this.closePanel();
        },
        postUpdateTableWidget({ origin, dimensionIds, updatedWidget }) {
            const { breakdownUpdated, widgetRefresh$, resetWidgetData } = this;

            if (
                R.equals(origin.dimensionIds, dimensionIds) &&
                breakdownUpdated
                // when ids not changed but label changed
            ) {
                if (this.isReactDashboard) {
                    this.postIframeMessage({
                        type: IFRAME_EVENT_MESSAGE.REFRESH_WIDGET_DATA,
                        data: updatedWidget,
                    });
                } else {
                    widgetRefresh$ && widgetRefresh$.next();
                }

                this.breakdownUpdated = false;
            }

            resetWidgetData();

            this.closePanel();
        },
        updateGraphWidget() {
            const {
                breakdownList = [],
                dashboardFilters,
                widgetFilters,
                _saveWidget,
                targetWidget: widget,
                metricList,
                sourceList,
                isFunnel,
                graphOptions,
                widgetDateFilter: dateFilter,
                widgetComparisonDateFilter: comparisonDateFilter,
                metricFilters,
                advancedMetricFilters,
                showGraphGrid,
                showGraphLabel,
                showTrendline,
                isAnalytic,
                axesConfig,
                exposureSelectedFieldMap,
                exposureListMap,
                quickMenuVisible,
            } = this;

            const dimensionIds = pluckFieldKey(breakdownList);

            const isUndefined = (v) => typeof v === 'undefined';
            const propsToOmitIfUndefined = R.reject(isUndefined, {
                showGraphLabel,
                axesConfig,
            });

            // [REFACTOR] 단순화
            const {
                YAXIS,
                XAXIS,
                BUBBLE_SIZE,
                COMBO_YAXIS_1,
                COMBO_YAXIS_2,
                COMBO_YAXIS_3,
                BREAKDOWN: FILTER_TYPE_BREAKDOWN,
                DATE_BREAKDOWN: FILTER_TYPE_DATE_BREAKDOWN,
            } = GRAPH_FILTER_TYPES;

            // Build updatedGraphOptions from exposureSelectedFieldMap
            let updatedGraphOptions = { ...graphOptions };
            const isComboChart =
                graphOptions.selectedGraphType === CHART_TYPES.COMBO_CHART;

            if (isComboChart) {
                const comboChart = {};
                const toAxisEntry = (field) => ({
                    metric: field?.[FIELD_KEY] ?? '',
                    type: field?.type || 'bar',
                    axisPosition: field?.axisPosition || 'left',
                });

                const yAxis1 = toAxisEntry(
                    exposureSelectedFieldMap[COMBO_YAXIS_1]
                );
                const yAxis2 = toAxisEntry(
                    exposureSelectedFieldMap[COMBO_YAXIS_2]
                );
                const yAxis3 = toAxisEntry(
                    exposureSelectedFieldMap[COMBO_YAXIS_3]
                );

                comboChart.yAxis1 = yAxis1;
                comboChart.yAxis2 = yAxis2;
                comboChart.yAxis3 = yAxis3;

                updatedGraphOptions = { ...graphOptions, comboChart };
            } else {
                const xAxisField = exposureSelectedFieldMap[XAXIS];
                const yAxisField = exposureSelectedFieldMap[YAXIS];
                const bubbleSizeField = exposureSelectedFieldMap[BUBBLE_SIZE];
                const dateBreakdownField =
                    exposureSelectedFieldMap[FILTER_TYPE_DATE_BREAKDOWN];

                updatedGraphOptions.selectedxAxis = xAxisField ?? '';
                updatedGraphOptions.selectedMetric = yAxisField ?? '';
                updatedGraphOptions.selectedBubbleSize = bubbleSizeField ?? '';
                updatedGraphOptions.dateBreakdown = dateBreakdownField ?? '';
            }

            // dimensionId: GRAPH widget은 exposureSelectedFieldMap[BREAKDOWN]에서 가져옴
            let dimensionId;
            const breakdownField =
                exposureSelectedFieldMap[FILTER_TYPE_BREAKDOWN];
            if (breakdownField) {
                dimensionId = breakdownField;
            }

            // exposureList: string[] 상태인 key만 저장 (null/undefined는 기존 widget 값 유지)
            const exposureList = Object.entries(exposureListMap).reduce(
                (acc, [fieldListKey, list]) => {
                    if (Array.isArray(list)) acc[fieldListKey] = list;
                    return acc;
                },
                { ...widget.exposureList }
            );

            let updatedWidget = R.mergeDeepLeft(
                {
                    filterTagList: dashboardFilters,
                    groupFilters: widgetFilters,
                    dimensionIds: dimensionId ? [dimensionId] : [],
                    metricWhereConditions: metricFilters,
                    advancedMetricWhereConditions: makeNumberFromFilter(
                        advancedMetricFilters
                    ),
                    showGraphGrid,
                    showTrendline,
                    isCustomWidget: isAnalytic,
                    ...updatedGraphOptions,
                    exposureList,
                    ...propsToOmitIfUndefined,
                },
                widget
            );

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            updatedWidget = R.compose(
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.assocPath(
                    [
                        'widgetStyles',
                        'uniqueSettings',
                        'quickMenuItem',
                        'visible',
                    ],
                    quickMenuVisible
                ),
                R.mergeLeft({ adAccounts, dateFilter, comparisonDateFilter })
            )(updatedWidget);

            if (isFunnel) {
                const columns = pluckFieldKey(metricList);
                updatedWidget = R.mergeLeft({ columns }, updatedWidget);
            }

            const isEqual = checkIsWidgetEqual(widget, updatedWidget);

            if (!isEqual) {
                _saveWidget({ widget: updatedWidget });
            }

            this.closePanel();
        },
        updateGoalWidget() {
            const {
                editingWidget: widget,
                metricList,
                updateEditableHeaderList,
                sourceList,
                dashboardFilters,
                widgetFilters,
                widgetDateFilter: dateFilter,
                widgetComparisonDateFilter: comparisonDateFilter,
                _saveWidget,
                forecastEnabled,
                isAnalytic,
            } = this;

            updateEditableHeaderList({ value: metricList });

            const { childWidgets = [] } = widget;

            const selectedMetricChildWidgets = R.compose(
                R.uniqBy(R.prop(FIELD_KEY))
            )(metricList);

            const goalChildWidgetMap = R.indexBy(R.prop('id'))(childWidgets);
            const updatedChildWidgets = selectedMetricChildWidgets.map(
                (nextGoalChildWidget, index) => {
                    const { id } = nextGoalChildWidget;
                    const prevGoalChildWidget = goalChildWidgetMap[id];
                    if (!prevGoalChildWidget) {
                        return {
                            alias: '',
                            id: uuidv4(),
                            metric: nextGoalChildWidget[FIELD_KEY],
                            goalInfo: nextGoalChildWidget.goalInfo,
                            goal: parseFloat(nextGoalChildWidget.goal) || '',
                            order: index,
                            shape: 'A',
                            barType: 'line',
                            type: GOAL_CHILD,
                        };
                    }

                    nextGoalChildWidget = R.compose(
                        R.omit(['key', 'isCustom']),
                        R.mergeRight(prevGoalChildWidget),
                        R.assoc('order', index)
                    )(nextGoalChildWidget);

                    return nextGoalChildWidget;
                }
            );

            const adAccounts = getSelectedAdAccounts(sourceList);
            const selectedChannels = getSelectedChannels(adAccounts);

            const updatedWidget = R.compose(
                R.assocPath(
                    ['mode'],
                    forecastEnabled
                        ? GoalWidgetMode.FORECAST
                        : GoalWidgetMode.NORMAL
                ),
                R.assoc('childWidgets', updatedChildWidgets),
                R.assoc('filterTagList', dashboardFilters),
                R.assoc('groupFilters', widgetFilters),
                R.assoc('adAccounts', adAccounts),
                R.when(
                    R.always(this.isTemplateView),
                    R.assoc('channelsForTemplateV2', selectedChannels)
                ),
                R.assoc('dateFilter', dateFilter),
                R.assoc('comparisonDateFilter', comparisonDateFilter),
                R.assoc('isCustomWidget', isAnalytic)
            )(widget);

            const isEqual = checkIsWidgetEqual(widget, updatedWidget);

            if (!isEqual) {
                _saveWidget({ widget: updatedWidget });
            }

            this.closePanel();
        },
        updateState({ key, value = [] }) {
            value = Object.freeze(value);

            switch (key) {
                case BREAKDOWN: {
                    this.breakdownList = value;
                    break;
                }
                case CREATIVE_BREAKDOWN: {
                    this.creativeDimensionIds = value;
                    break;
                }
                case TABLE_METRIC:
                case SINGULAR_METRIC:
                case GOAL_METRICS:
                    this.metricList = value;
                    break;
                case METRIC_FILTER:
                    this.metricFilters = value;
                    break;
                case WIDGET_FILTER:
                    this.widgetFilters = value;
                    break;
                case DATA_SOURCE:
                    this.sourceList = value;
                    break;
                case DATE_FILTER: {
                    const {
                        widgetDateFilter = {},
                        widgetComparisonDateFilter = {},
                    } = value;

                    this.widgetDateFilter = widgetDateFilter;
                    this.widgetComparisonDateFilter =
                        widgetComparisonDateFilter;
                    break;
                }
                case GRAPH_OPTIONS: {
                    const graphType = R.propOr('', 'selectedGraphType', value);
                    const widgetBreakdown = R.pathOr(
                        '',
                        ['dimensionIds', 0],
                        this.editingWidget
                    );
                    const resetBreakdown =
                        widgetBreakdown === 'all' &&
                        ![BAR, LINE, HORIZONTAL_BAR].includes(graphType);

                    if (resetBreakdown) {
                        this.resetBreakdownSelect('channel');
                    }

                    this.graphOptions = value;
                    break;
                }
                case FORECAST:
                    this.forecastEnabled = value;
                    break;
                case ONOFF:
                    this.hideGraph = value;
                    break;
                case COMPARISON_BY_DATE:
                    this.comparisonByDate = value;
                    break;
                case ADVANCED_METRIC_FILTER:
                    this.advancedMetricFilters = value;
                    break;
                case CREATIVE_CNT:
                    this.creativeCnt = value;
                    break;
                case GRAPH_GRID:
                    this.showGraphGrid = value;
                    break;
                case GRAPH_LABEL:
                    this.showGraphLabel = value;
                    break;
                case TRENDLINE:
                    this.showTrendline = value;
                    break;
                case QUICK_MENU_VISIBLE:
                    this.quickMenuVisible = value;
                    break;
                case FILTER_WIDGET_OPTIONS:
                    this.filterWidgetOptions = value;
                    break;
                case BLEND_SETTING_TOGGLE:
                    this.isCustomWidget = value;
                    break;
                case TABLE_TRANSPOSE_TOGGLE:
                    this.isTransposed = value;
                    break;

                case AXES_CONFIG:
                case COMBO_AXES_CONFIG: {
                    const updatedAxesConfig = R.mergeDeepLeft(
                        value,
                        this.axesConfig ?? {}
                    );
                    this.axesConfig = updatedAxesConfig;
                    break;
                }
            }
        },
        updateExposureFieldListMap({ key, value }) {
            this.exposureListMap = {
                ...this.exposureListMap,
                [key]: value,
            };
        },
        updateExposureSelectedFieldMap({ exposureListKey, value }) {
            this.exposureSelectedFieldMap = {
                ...this.exposureSelectedFieldMap,
                [exposureListKey]: value,
            };
        },
        observeEditingWidget$() {
            const {
                generateWatchNew$: gen$,
                rawListByType,
                updateState,
                widgetType,
                dateFilterProps,
                isAnalytic,
                currentWidgets,
                exposureKeyConfig,
            } = this;

            const widget$ = gen$('editingWidget').pipe(
                debounceTime(100),
                map((props) => {
                    return {
                        dimension: props.dimensionIds,
                        ...props,
                    };
                })
            );

            const makeCurrentAdAccount = (adAccounts, baseList) => {
                if (!adAccounts) {
                    return R.map(
                        (v) =>
                            R.assoc(
                                'selected',
                                isAnalytic
                                    ? checkIsAnalyticSource(v)
                                    : !checkIsAnalyticSource(v),
                                v
                            ),
                        baseList
                    );
                }

                const adAccountsMap = R.indexBy(getAccountKey, adAccounts);

                const updatedBaseList = baseList.map((item) => {
                    const accountKey = getAccountKey(item);
                    const matchingAdAccount = adAccountsMap[accountKey];

                    if (matchingAdAccount) {
                        delete adAccountsMap[accountKey];

                        return assocSelected(item);
                    }
                    return item;
                });

                const remainingAdAccounts = R.map(assocSelected)(
                    Object.values(adAccountsMap)
                );

                return [...updatedBaseList, ...remainingAdAccounts];
            };

            const makeInitialLinkedWidgetIds = () => {
                return R.compose(
                    R.map(R.prop('id')),
                    R.filter(R.complement(isExcludedForLinkedWidget)),
                    R.defaultTo([])
                )(currentWidgets);
            };

            this.$subscribeTo(widget$, (args) => {
                const {
                    dimension = [],
                    adAccounts,
                    metricWhereConditions,
                    groupFilters = [], // widget filter
                    widgetGroupFilters = [], // abTesting filter
                    columns = [],
                    metric,
                    sort = {},
                    childWidgets = [],
                    dateFilter: widgetDateFilter = {
                        ...DEFAULT_DATE_FILTER,
                        ...dateFilterProps.rawDateFilter,
                    },
                    comparisonDateFilter: widgetComparisonDateFilter = {},
                    mode,
                    isCustomWidget = false,
                    hideGraph = false,
                    showGraphGrid = true,
                    showTrendline = false,
                    comparisonByDate = false,
                    columnDimensionIds = [],
                    advancedMetricWhereConditions,
                    creativeCnt = 10,
                    isTransposed = false,
                    widgetStyles,

                    // undefined by default
                    showGraphLabel,
                    axesConfig,

                    // rest props
                    ...rest
                } = args;

                if (widgetType === GRAPH) {
                    const quickMenuVisible = R.pathOr(
                        true,
                        ['uniqueSettings', 'quickMenuItem', 'visible'],
                        widgetStyles
                    );
                    updateState({
                        key: QUICK_MENU_VISIBLE,
                        value: quickMenuVisible,
                    });
                    const graphOptions = R.pickAll(
                        Object.keys(GRAPH_OPTION_DEFAULT_VALUE),
                        { sort, ...rest }
                    );

                    updateState({
                        key: GRAPH_OPTIONS,
                        value: graphOptions,
                    });

                    updateState({
                        key: GRAPH_GRID,
                        value: showGraphGrid,
                    });

                    updateState({
                        key: TRENDLINE,
                        value: showTrendline,
                    });

                    if (!R.isNil(showGraphLabel)) {
                        updateState({
                            key: GRAPH_LABEL,
                            value: showGraphLabel,
                        });
                    }

                    if (isValueExist(axesConfig)) {
                        updateState({
                            key: AXES_CONFIG,
                            value: axesConfig,
                        });
                    }
                }

                if (widgetType === FILTER) {
                    const {
                        linkedWidgetIds,
                        allowMultiSelect,
                        sortAlphabetically,
                        availableLabels,
                        subType,
                        groupId,
                        by,
                        condition,
                    } = rest;

                    const { filterWidgetOptions } = this;

                    const normalizedOptionsFromWidget =
                        normalizeWidgetDropdownConfig({
                            linkedWidgetIds,
                            allowMultiSelect,
                            sortAlphabetically,
                            availableLabels,
                            subType,
                            groupId,
                            by,
                            condition,
                        });

                    const optionsFromState = {
                        ...filterWidgetOptions,
                        linkedWidgetIds:
                            filterWidgetOptions.linkedWidgetIds === null
                                ? makeInitialLinkedWidgetIds()
                                : filterWidgetOptions.linkedWidgetIds,
                    };

                    const mergedFilterWidgetOptions = {
                        ...optionsFromState,
                        ...normalizedOptionsFromWidget,
                        linkedWidgetIds: R.isNil(linkedWidgetIds)
                            ? optionsFromState.linkedWidgetIds
                            : linkedWidgetIds,
                        allowMultiSelect: R.isNil(allowMultiSelect)
                            ? optionsFromState.allowMultiSelect
                            : allowMultiSelect,
                        sortAlphabetically: R.isNil(sortAlphabetically)
                            ? optionsFromState.sortAlphabetically
                            : sortAlphabetically,
                    };
                    if (isValueExist(mergedFilterWidgetOptions)) {
                        updateState({
                            key: FILTER_WIDGET_OPTIONS,
                            value: mergedFilterWidgetOptions,
                        });
                    }

                    return;
                }

                if (R.includes(widgetType, BLEND_ENABLE_WIDGET_TYPES)) {
                    updateState({
                        key: BLEND_SETTING_TOGGLE,
                        value: isCustomWidget,
                    });
                }

                updateState({
                    key: ONOFF,
                    value: hideGraph,
                });

                updateState({
                    key: CREATIVE_CNT,
                    value: creativeCnt,
                });

                updateState({
                    key: FORECAST,
                    value: mode === GoalWidgetMode.FORECAST,
                });

                const currentAdAccounts = makeCurrentAdAccount(
                    adAccounts,
                    rawListByType(DATA_SOURCE)
                );

                updateState({
                    key: DATA_SOURCE,
                    value: currentAdAccounts,
                });

                const _groupFilters =
                    widgetType === AB_TESTING
                        ? widgetGroupFilters
                        : groupFilters;

                updateState({
                    key: WIDGET_FILTER,
                    value: _groupFilters,
                });

                updateState({
                    key: METRIC_FILTER,
                    value: metricWhereConditions,
                });

                updateState({
                    key: DATE_FILTER,
                    value: { widgetDateFilter, widgetComparisonDateFilter },
                });

                updateState({
                    key: COMPARISON_BY_DATE,
                    value: comparisonByDate,
                });

                // columnDimensionIds 상태 설정
                this.columnDimensionIds = columnDimensionIds;

                updateState({
                    key: ADVANCED_METRIC_FILTER,
                    value: advancedMetricWhereConditions,
                });

                let _columns = columns;

                switch (widgetType) {
                    case SINGLE_METRIC:
                    case OVERVIEW_CHILD:
                        _columns = metric ? [metric] : [];
                        break;
                    case GOAL:
                        _columns = childWidgets;
                        break;
                    case OVERVIEW:
                        _columns = R.pluck('metric', childWidgets);
                        break;
                }

                /**
                 * Disable metric selection when no data source is selected after widget creation
                 */
                const selectedAdAccounts = filterSelected(currentAdAccounts);
                if (!adAccounts && !isValueExist(selectedAdAccounts)) {
                    _columns = [];
                }

                const hasColumns = _columns.filter(Boolean).length > 0;

                if (
                    widgetType === PIVOT_TABLE ||
                    widgetType === PIVOT_TABLE_ANALYTIC
                ) {
                    updateState({
                        key: TABLE_TRANSPOSE_TOGGLE,
                        value: isTransposed,
                    });
                }

                /**
                 * breakdown
                 */
                if (widgetType === CREATIVE) {
                    updateState({
                        key: CREATIVE_BREAKDOWN,
                        value: dimension,
                    });
                } else {
                    // dimensionIds는 string|number 배열이어야 하지만
                    // 이전 버그로 object가 저장된 경우를 방어적으로 처리
                    const formattedDimension = dimension
                        .map((d) =>
                            typeof d === 'object' && d !== null ? d.id : d
                        )
                        .map(makeBreakdownFormat);
                    updateState({
                        key: BREAKDOWN,
                        value: formattedDimension,
                    });
                }

                /**
                 * metric
                 */
                if (hasColumns) {
                    if (widgetType === GOAL) {
                        const formatedColumns =
                            _columns.map(makeGoalMetricFormat);
                        updateState({
                            key: GOAL_METRICS,
                            value: formatedColumns,
                        });
                    } else {
                        const formatedColumns = _columns.map(makeMetricFormat);
                        updateState({
                            key: TABLE_METRIC,
                            value: formatedColumns,
                        });
                    }
                }

                /**
                 * Initialize data for exposure setting
                 */

                if (widgetType === CREATIVE || widgetType === GRAPH) {
                    const { exposureListMap, exposureSelectedFieldMap } =
                        Object.entries(exposureKeyConfig).reduce(
                            (acc, [filterKey, config]) => {
                                const { exposureSettingType } = config;
                                const { selectedField, fieldList } =
                                    exposureSettingType === 'comboMetric'
                                        ? makeComboExposureData({
                                              key: filterKey,
                                              widget: args,
                                          })
                                        : makeExposureData({
                                              key: filterKey,
                                              widget: args,
                                          });

                                // exposureListMap: fieldListKey로 키잉 (리스트 공유 시 한 번만 기록)
                                const { fieldListKey } = config;
                                if (!acc.exposureListMap[fieldListKey]) {
                                    acc.exposureListMap[fieldListKey] =
                                        fieldList;
                                }
                                // exposureSelectedFieldMap: filterKey로 키잉 (축별 독립)
                                // comboMetric만 Object, metric/breakdown은 string으로 관리
                                acc.exposureSelectedFieldMap[filterKey] =
                                    exposureSettingType === 'comboMetric'
                                        ? selectedField
                                        : selectedField?.[FIELD_KEY] ?? '';

                                return acc;
                            },
                            {
                                exposureListMap: {},
                                exposureSelectedFieldMap: {},
                            }
                        );

                    this.exposureListMap = exposureListMap;
                    this.exposureSelectedFieldMap = exposureSelectedFieldMap;
                }
            });
        },
        observeRawData$() {
            const {
                generateWatchNew$: gen$,
                secondPanelVisibility$,
                setHeaderPanelLoading: setLoading,
                editingWidgetId: id,
                updateListFromRaw,
            } = this;

            const updateParam = R.curryN(2, (type, value) => ({ type, value }));

            const combined$ = (() => {
                const accounts$ = gen$('sourceOptions').pipe(
                    map(updateParam(DATA_SOURCE))
                );

                return combineLatest(accounts$).pipe(debounceTime(300));
            })();

            const secondPanel$ = secondPanelVisibility$.pipe(
                filter(isValueExist)
            );
            const combinedVisibility$ = combined$.pipe(
                delayWhen(() => secondPanel$),
                pairwise(),
                scan(
                    (acc, value) => {
                        return {
                            count: acc.count + 1,
                            value,
                        };
                    },
                    {
                        count: 0,
                        value: undefined,
                    }
                ),
                tap(() => setLoading({ id, value: true })),
                map(({ count, value: [prev, next] }) => {
                    if (count > 1) {
                        return R.difference(next, prev);
                    }
                    return next;
                }),
                filter((value) => {
                    const hasValue = isValueExist(value);

                    if (!hasValue) {
                        setLoading({ id, value: false });
                    }

                    return hasValue;
                })
            );

            this.$subscribeTo(combinedVisibility$, (values = []) => {
                values.forEach(updateListFromRaw);
                setLoading({ id, value: false });
            });
        },
        selectedListByType(key) {
            return R.compose(filterSelected, this.currentListByType)(key);
        },
        currentListByType(key) {
            switch (key) {
                case DATA_SOURCE:
                    return this.sourceList;
                case METRIC_FILTER:
                    return this.metricFilters;
                case WIDGET_FILTER:
                    return this.widgetFilters;
                /**
                 * fields
                 */
                case BREAKDOWN:
                    return this.breakdownList;
                case TABLE_METRIC:
                case SINGULAR_METRIC:
                case GOAL_METRICS:
                    return this.metricList;
                case SORTING_METRIC_EXPOSURE: {
                    const { fieldListKey, defaultFieldList } =
                        EXPOSURE_KEY_CONFIG[key];
                    const strings = this.exposureListMap[fieldListKey];
                    if (!Array.isArray(strings)) {
                        // null/undefined: 미설정 상태 → defaultFieldList 표시
                        const selectedField =
                            this.exposureSelectedFieldMap[
                                SORTING_METRIC_EXPOSURE
                            ];
                        const effectiveStrings = [...defaultFieldList];
                        const selectedKey = selectedField;
                        if (
                            selectedKey &&
                            !effectiveStrings.includes(selectedKey)
                        ) {
                            effectiveStrings.unshift(selectedKey);
                        }
                        return effectiveStrings.map(makeMetricFormat);
                    }
                    return strings.map(makeMetricFormat);
                }
                default:
                    return [];
            }
        },
        rawListByType(key) {
            // const shouldHideDateBreakdowns =
            //     widgetType === GRAPH && !isComboChart;

            switch (key) {
                case DATA_SOURCE:
                    return this.sourceOptions;
                default:
                    return [];
            }
        },
        localeRefinerByType(key) {
            switch (key) {
                case SORTING_METRIC_EXPOSURE:
                    return this.getMetricName;
                case WIDGET_FILTER:
                    return (key) => this.getBreakdownName({ key });
                case DATA_SOURCE:
                    return refineSourceNameWithDeprecated.bind(this);
                default:
                    return R.prop('name');
            }
        },
        watchDataSource() {
            const {
                editingWidgetId: id,
                selectedDataSources$,
                resetDataOnsource,
                selectedSource,
                selectedSourceIgnoringScope,
            } = this;

            selectedDataSources$.next({ id, dataSources: selectedSource });

            /**
             * Judge on the unscoped selection: `resetDataOnsource` wipes
             * breakdown/metric/filter state and the panel persists that on save,
             * so a teamspace scope that merely hides every selected source must
             * not trigger it. Display stays scoped, the stored setting survives.
             */
            if (!isValueExist(selectedSourceIgnoringScope)) {
                resetDataOnsource();
            }
        },
        resetBreakdownSelect(targetBreakdown = '') {
            const updatedBreakdownList = [makeBreakdownFormat(targetBreakdown)];

            this.breakdownList = updatedBreakdownList;
        },
        resetDataSource() {
            const { sourceList } = this;

            this.sourceList = sourceList.map((source) => ({
                ...source,
                selected: false,
            }));
        },
        resetDataOnsource(forceReset) {
            if (this.isAnalytic || forceReset) {
                this.breakdownList = [];
                this.creativeAvailableDimensionList = [];
                this.metricList = [];
                this.dashboardFilters = [];
                this.widgetFilters = [];
                this.metricFilters = [];
            }

            this.exposureListMap = Object.values(this.exposureKeyConfig).reduce(
                (acc, { fieldListKey }) => {
                    acc[fieldListKey] = [];
                    return acc;
                },
                {}
            );
            this.exposureSelectedFieldMap = {};
        },
        updateListFromRaw({ type, value }) {
            const { currentListByType, updateState } = this;

            const prevList = currentListByType(type);
            let list = prevList;
            const indexKey = makeIndexKey(type);

            const valueIndexed = R.indexBy(R.prop(indexKey), value);

            list = list.map((v) => {
                const _v = valueIndexed[v[indexKey]];
                return _v ? { ...v, ..._v } : v;
            });

            const _differenceWith = (list1, list2) => {
                const set2 = new Set(list2.map((y) => y[indexKey]));
                return list1.filter((x) => !set2.has(x[indexKey]));
            };

            const left = _differenceWith(value, list);

            if (left.length) {
                list = Object.freeze(list.concat(left));
            }

            updateState({
                key: type,
                value: list,
            });
        },
        makePropByType(key) {
            const {
                isTemplateView,
                selectedListByType,
                updateState,
                currentListByType,
                removeOverviewChildWidget,
                rawListByType,
                localeRefinerByType,
                editingWidget,
                widgetType,
                widgetDateFilter,
                widgetComparisonDateFilter,
                resetDataOnsource,
                graphOptions,
                breakdownList,
                widget,
                forecastEnabled,
                hideGraph,
                showGraphGrid,
                showGraphLabel,
                showTrendline,
                quickMenuVisible,
                isCustomWidget,
                isColumnLikeGraph,
                widgetFilters,
                comparisonByDate,
                columnDimensionIds,
                advancedMetricFilters,
                panelLoading,
                secondPanelLoading,
                activeOutsideClick,
                analyticGenerateOption,
                isTransposed,
                axesConfig,

                /**
                 * field panel props
                 */
                selectedSource,
                currentBreakdownIds,
                isAnalytic,
                currentViewId,
                teamspaceId,
            } = this;

            const commonProps = {
                attrs: {
                    isTemplateView,
                    compKey: key,
                    isCustomWidget,
                    selectedList: selectedListByType(key),
                    currentList: currentListByType(key),
                    rawList: rawListByType(key),
                    translator: localeRefinerByType(key),
                    isItemSortable: true,
                    currentListByType,
                    selectedListByType,
                    localeKey: LOCALE_MAP[key],
                    indexKey: makeIndexKey(key),
                    editingWidget,
                    widgetType,
                    panelLoading,
                    secondPanelLoading,
                    widgetDateFilter,
                    widgetComparisonDateFilter,
                    graphOptions,
                    breakdownList,
                    widget,
                    forecastEnabled,
                    hideGraph,
                    showGraphGrid,
                    showGraphLabel:
                        showGraphLabel ??
                        getDefaultShowGraphLabel({
                            graphType: graphOptions.selectedGraphType,
                        }),
                    showTrendline,
                    quickMenuVisible,
                    isColumnLikeGraph,
                    originWidgetFilters: widgetFilters,
                    disabled: this.optionDisabled(key),
                    comparisonByDate,
                    columnDimensionIds,
                    advancedMetricFilters,
                    activeOutsideClick,
                    analyticGenerateOption,
                    isTransposed,
                    axesConfig,

                    /**
                     * field panel props
                     */
                    panelKey: FIELD_PANEL_KEY[key]?.(),
                    selectedSource,
                    currentBreakdownIds,
                    isAnalytic,
                    currentViewId,
                    teamspaceId,
                },
                on: {
                    updateByKey: updateState,
                    update: (value) => {
                        updateState({ key, value });
                    },
                    delete: removeOverviewChildWidget,
                    resetTableValues: resetDataOnsource,
                    'outsideClick:active': (v) => {
                        this.activeOutsideClick = v;
                    },
                    updateColumnDimensionIds: (value) => {
                        this.columnDimensionIds = value;
                    },
                },
            };

            return this.addAdditionalProps(key, commonProps);
        },
        addAdditionalProps(key, props) {
            switch (key) {
                case CREATIVE_BREAKDOWN: {
                    return R.compose(
                        R.assocPath(
                            ['attrs', 'creativeDimensionIds'],
                            this.creativeDimensionIds
                        ),
                        R.assocPath(
                            ['attrs', 'creativeAvailableDimensionList'],
                            this.creativeAvailableDimensionList
                        )
                    )(props);
                }
                case CREATIVE_CNT: {
                    return R.assocPath(
                        ['attrs', 'creativeCnt'],
                        this[CREATIVE_CNT],
                        props
                    );
                }
                case FILTER_WIDGET_OPTIONS: {
                    return R.mergeDeepLeft(
                        {
                            attrs: {
                                filterWidgetOptions: this.filterWidgetOptions,
                            },
                        },
                        props
                    );
                }
                case SORTING_METRIC_EXPOSURE: {
                    const { fieldListKey, defaultFieldList } =
                        EXPOSURE_KEY_CONFIG[key];

                    return R.compose(
                        R.assocPath(
                            ['attrs', 'optionTitle'],
                            this.$t('NEW_DASHBOARD.sorting_order_of_columns')
                        ),
                        R.assocPath(['attrs', 'exposureListKey'], fieldListKey),
                        R.assocPath(
                            ['attrs', 'selectedField'],
                            this.exposureSelectedFieldMap[key] ?? ''
                        ),
                        R.assocPath(
                            ['attrs', 'exposureList'],
                            this.exposureListMap[fieldListKey]
                        ),
                        R.assocPath(['attrs', 'defaultList'], defaultFieldList),
                        R.assocPath(
                            ['on', 'update:exposureListMap'],
                            this.updateExposureFieldListMap
                        ),
                        R.assocPath(
                            ['on', 'update:exposureSelectedFieldMap'],
                            (value) => {
                                this.updateExposureSelectedFieldMap({
                                    exposureListKey: key,
                                    value,
                                });
                            }
                        )
                    )(props);
                }
                case GRAPH_OPTIONS: {
                    return R.compose(
                        R.assocPath(
                            ['attrs', 'exposureListMap'],
                            this.exposureListMap
                        ),
                        R.assocPath(
                            ['attrs', 'exposureSelectedFieldMap'],
                            this.exposureSelectedFieldMap
                        ),
                        R.assocPath(
                            ['attrs', 'pendingExposureListKey'],
                            this.pendingExposureListKey
                        ),
                        R.assocPath(
                            ['on', 'update:exposureListMap'],
                            this.updateExposureFieldListMap
                        ),
                        R.assocPath(
                            ['on', 'update:exposureSelectedFieldMap'],
                            this.updateExposureSelectedFieldMap
                        )
                    )(props);
                }
                default:
                    return props;
            }
        },
        observeCreativeDatasource$() {
            const { generateWatchNew$: gen$, widgetType } = this;

            const isAnalytic$ = gen$('isAnalytic').pipe(filter(isValueExist));
            const assetIds$ = gen$('selectedAssetIds').pipe(
                distinctUntilChanged((a, b) =>
                    R.equals(sortLongString(a), sortLongString(b))
                )
            );

            const selectedAssetIds$ = combineLatest([
                assetIds$,
                isAnalytic$,
            ]).pipe(
                filter(() => widgetType === CREATIVE),
                debounceTime(1000)
            );

            this.$subscribeTo(selectedAssetIds$, ([assetIds, isAnalytic]) => {
                this.fetchCreativeAvailableDimensionList({
                    assetIds,
                    isAnalytic,
                });
            });
        },
        renderOptions() {
            const { makePropByType, refinedOptions = [] } = this;

            return (
                <S.OptionsWrapper>
                    {refinedOptions.map(([key, Comp]) => {
                        const props = makePropByType(key);

                        if (key === FILTER_WIDGET_OPTIONS) {
                            props.ref = key;
                        }

                        return <Comp {...props} />;
                    })}
                </S.OptionsWrapper>
            );
        },
        observeWidgetId$() {
            const { generateWatchNew$: gen$ } = this;
            let timeoutId;

            const id$ = gen$('widgetId').pipe(
                filter(R.identity),
                distinctUntilChanged(R.equals),
                tap(() => {
                    clearTimeout(timeoutId);
                    this.inPreparation = true;
                    timeoutId = setTimeout(() => {
                        this.inPreparation = false;
                    }, 600);
                })
            );

            this.$subscribeTo(id$, (id) => {
                console.log('widgetId changed', { id });
            });
        },
        async fetchCreativeAvailableDimensionList({ assetIds, isAnalytic }) {
            const { creativeDimensionIds, isSelectionNarrowedByScope } = this;

            const defaultBreakdown = isAnalytic ? 'organic:post' : 'ad';
            try {
                if (!isValueExist(assetIds)) {
                    /**
                     * An empty scoped selection is not proof that the user
                     * picked nothing — a teamspace scope that hides every
                     * selected source empties it too. Show zero candidates
                     * either way, but only rewrite the stored breakdown when
                     * the selection is genuinely empty.
                     */
                    if (!isSelectionNarrowedByScope) {
                        this.updateState({
                            key: CREATIVE_BREAKDOWN,
                            value: [defaultBreakdown],
                        });
                    }

                    this.creativeAvailableDimensionList = [];
                    return;
                }

                const creativeAvailableDimensionList =
                    await DashboardService.getCreativeAvailableDimensions({
                        assetIds,
                        widgetType: isAnalytic ? 'normal' : 'marketing',
                    });

                /**
                 * Same reasoning for a partial scope: the list was fetched for
                 * a subset of the widget's sources, so "the saved dimension is
                 * not available" is a statement about that subset, not about
                 * the widget. Replacing the stored dimension on that evidence
                 * would delete a setting the scope was only meant to hide.
                 */
                const selectedDimensionId = R.head(creativeDimensionIds);
                if (
                    !isSelectionNarrowedByScope &&
                    !R.includes(
                        selectedDimensionId,
                        creativeAvailableDimensionList
                    )
                ) {
                    const head =
                        R.head(creativeAvailableDimensionList) ??
                        defaultBreakdown;
                    this.updateState({
                        key: CREATIVE_BREAKDOWN,
                        value: [head],
                    });
                }

                this.creativeAvailableDimensionList =
                    creativeAvailableDimensionList;
            } catch (error) {
                console.error(error);
            }
        },
        analyticGenerateOption(item) {
            const { isAllValidBlended } = this;

            let options = [];
            let message = '';

            const isMarketing =
                !checkIsAnalyticSource(item) || mappingActivated(item);

            if (!isAllValidBlended && isMarketing) {
                options = [UNSELECT_ALREADY_SELECTED];
                message = this.$t(
                    'NEW_DASHBOARD.change_source_mode_modal_basic_desc'
                );
            }

            const isOrganic = R.propEq('dataSourcePrefix', 'organic', item);

            /** creative widget */
            if (this.widgetType === CREATIVE && !isOrganic) {
                options = [UNSELECT_ALREADY_SELECTED];
                message = this.$t(
                    'NEW_DASHBOARD.change_source_mode_modal_basic_desc'
                );
            }

            return { options, message };
        },

        filterDeletedChannelAccounts(adAccounts) {
            if (!adAccounts) return undefined;

            if (!this.dataSources) return adAccounts;
            // to filter deleted channel ad account

            return R.filter(
                (source) => this.datasourceMapByAssetId[source.assetId],
                adAccounts
            );
        },
        optionDisabled(key) {
            const { widgetType } = this;

            if (widgetType === AB_TESTING && key === WIDGET_FILTER) {
                return true;
            }

            if (widgetType === PIVOT_TABLE && key === TABLE_OPTIONS) {
                return (
                    isValueExist(this.columnDimensionIds) ||
                    this.comparisonByDate
                );
            }

            return false;
        },
    },
    mounted() {
        this.observeWidgetId$();
        this.observeEditingWidget$();
        this.observeRawData$();
        this.observeCreativeDatasource$();
    },
    render() {
        const {
            requestClose,
            renderOptions,
            inPreparation,
            widgetType,
            activeOutsideClick,
            panelLoading,
        } = this;

        const PROPS = {
            attrs: {
                hasButtons: false,
                onCancel: requestClose,
                closeOutside: activeOutsideClick,
                closeBlockClasses: [
                    'widget--gear',
                    'adriel-dialog',
                    'noti-boxes',
                    'ant-dropdown',
                ],
                placement: 'right',
                editAreaPadding: '20px 28px',
            },
        };

        const $abDesc = widgetType === AB_TESTING && (
            <S.AbTeestingDescription
                domPropsInnerHTML={
                    '*' + this.$t('FILTERS.panel_disabled_description')
                }
            />
        );

        return (
            <S.ComponentWrapper>
                <Drawer {...PROPS} style={{ transform: 'unset' }}>
                    <Header isLoading={panelLoading} slot="header" />
                    {inPreparation ? (
                        <S.LoaderWrapper>
                            <RingLoader />
                        </S.LoaderWrapper>
                    ) : (
                        [renderOptions(), $abDesc]
                    )}
                </Drawer>
            </S.ComponentWrapper>
        );
    },
};

export default withHMR(module, WidgetSettingPanel, WidgetSettingPanel.name);
